import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle } from "../../api/asyncData";
import { getAiPreferences } from "../../api/commands/ai";
import * as AiChat from "../../widgets/aiChat";
import type { Page, PageReturn } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

const foldChat = Update.foldChild({
	update: (chat: AiChat.Model, message: AiChat.Message) =>
		AiChat.update(chat, message),
	read: (model: Model) => Option.some(model.chat),
	write: (model: Model, chat: AiChat.Model) => evo(model, { chat: () => chat }),
	toParentMessage: (message: AiChat.Message) =>
		Message.GotChatMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
});

export const FetchAi = getAiPreferences.command(Message.CompletedFetchAi);

/** True only once the circle's preference reads off; loading or failing leaves chat usable (the server still refuses). */
export const aiTurnedOff = (model: Model): boolean =>
	AsyncData.getOrElse(model.ai, () => null)?.enabled === false;

/** The whole page is the nested chat widget; v1 focused the textarea on mount. */
export const page: Page<Model, Message> = {
	init: (_route, shared) => ({
		model: { page: "Chat", chat: AiChat.init(), ai: AsyncData.Loading() },
		commands: [
			...Command.mapMessages([AiChat.FocusInput()], (message) =>
				Message.GotChatMessage({ message }),
			),
			FetchAi({ generation: shared.generation }),
		],
	}),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<PageReturn<Model, Message>>(message, {
			GotChatMessage: ({ message }) => foldChat(model, message),
			CompletedFetchAi: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: { ...model, ai: settle(model.ai, result) } },
		}),
};

/** Lifted to the root Subscription: `Some` while a request is pending. */
export const streamDependencies = (model: Model) =>
	AiChat.streamDependencies(model.chat);
